import type {
  CloseRequestLayer,
  CloseWatcherWindow,
  NativeCloseWatcher,
} from '../types/close-request.type';
import { getTopmostCloseLayer } from '../utils/topmost-close-layer.util';

const layers: CloseRequestLayer[] = [];
let watcher: NativeCloseWatcher | undefined;
let rearmTimer: number | undefined;
let suspended = false;
const documentEscapeOwners = new Set<symbol>();

function destroyWatcher() {
  watcher?.destroy();
  watcher = undefined;
  window.clearTimeout(rearmTimer);
  rearmTimer = undefined;
}

function ensureWatcher() {
  if (watcher || rearmTimer !== undefined || suspended || layers.length === 0) {
    return;
  }
  const browser: CloseWatcherWindow = window;
  if (!browser.CloseWatcher) return;

  const activeWatcher = new browser.CloseWatcher();
  watcher = activeWatcher;
  activeWatcher.addEventListener('close', () => {
    if (watcher !== activeWatcher) return;
    watcher = undefined;
    // A close event consumes the native watcher, not the controlled UI state.
    // Rearm after native dispatch completes, including when Close asks for
    // confirmation or is non-dismissible. Never cancel a browser close event.
    rearmTimer = window.setTimeout(() => {
      rearmTimer = undefined;
      ensureWatcher();
    }, 0);
    getTopmostCloseLayer(layers)?.requestClose();
  });
}

function handleEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) {
    return;
  }
  if (documentEscapeOwners.size > 0 && !suspended) {
    // The real drag sensor must receive Escape to detach its pointer listeners.
    // Suppress native CloseWatcher dispatch, but leave propagation to the owner.
    event.preventDefault();
    return;
  }
  const layer = getTopmostCloseLayer(layers);
  if (!layer || suspended) return;
  // Own this real key event before library/React handlers. Preventing its
  // default also prevents a second, native CloseWatcher request from Escape.
  event.preventDefault();
  event.stopImmediatePropagation();
  if (!event.repeat) layer.requestClose();
}

function handlePageHide() {
  suspended = true;
  destroyWatcher();
}

function handlePageShow() {
  suspended = false;
  ensureWatcher();
}

/**
 * Register a visible layer with one native watcher and one Escape handler.
 * No history entries, navigation interception, keyboard detection or blur.
 * @param layer - Identity, ancestry and feature-owned close request callback.
 * @returns Idempotent cleanup for close, route unmount, or component unmount.
 */
export function registerCloseRequest(layer: CloseRequestLayer): () => void {
  layers.push(layer);
  if (layers.length === 1) {
    suspended = false;
    window.addEventListener('keydown', handleEscape, true);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
  }
  ensureWatcher();

  return () => {
    const index = layers.indexOf(layer);
    if (index < 0) return;
    layers.splice(index, 1);
    if (layers.length !== 0) return;
    destroyWatcher();
    window.removeEventListener('keydown', handleEscape, true);
    window.removeEventListener('pagehide', handlePageHide);
    window.removeEventListener('pageshow', handlePageShow);
  };
}

/**
 * Yield Escape to a document-level interaction (the existing dnd-kit sensor).
 * The owner must release on completion, cancellation and unmount. This prevents
 * shared dismissal without synthesizing events or leaving a sensor attached.
 * @returns Idempotent release of this interaction's Escape ownership.
 */
export function delegateEscapeToDocument(): () => void {
  const id = Symbol('document-escape-owner');
  documentEscapeOwners.add(id);
  return () => {
    documentEscapeOwners.delete(id);
  };
}
